/**
 * Session lifecycle — pure helpers, no persistence.
 * `src/db/auth-store.ts` owns the storage side and builds on these.
 */
import type { AuthSession, Device, UnlockMethod, User } from '../types/user';

/** A single active session per installation. */
export const SESSION_ID = 'current';

/** Offline-first: a session survives 30 days, unlocked by PIN on each launch. */
export const SESSION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export interface CreateSessionOptions {
  now?: number;
  unlockMethod?: UnlockMethod;
  ttlMs?: number;
}

export function createSession(
  user: User,
  device: Device,
  options: CreateSessionOptions = {},
): AuthSession {
  const now = options.now ?? Date.now();
  const ttlMs = options.ttlMs ?? SESSION_TTL_MS;
  const timestamp = new Date(now).toISOString();
  return {
    id: SESSION_ID,
    userId: user.id,
    deviceId: device.id,
    deviceFingerprint: device.deviceFingerprint,
    role: user.role,
    issuedAt: timestamp,
    expiresAt: new Date(now + ttlMs).toISOString(),
    lastSeenAt: timestamp,
    unlockMethod: options.unlockMethod ?? 'otp',
  };
}

export function isSessionExpired(session: AuthSession, now: number = Date.now()): boolean {
  return now >= Date.parse(session.expiresAt);
}

/** Returns a copy with a refreshed `lastSeenAt` (and optionally unlock method). */
export function touchSession(
  session: AuthSession,
  options: { now?: number; unlockMethod?: UnlockMethod } = {},
): AuthSession {
  const now = options.now ?? Date.now();
  return {
    ...session,
    lastSeenAt: new Date(now).toISOString(),
    unlockMethod: options.unlockMethod ?? session.unlockMethod,
  };
}

/**
 * A session is only usable when it has not expired, the account is still
 * approved and active, and it was issued to *this* device fingerprint.
 */
export function isSessionValidFor(
  session: AuthSession | null | undefined,
  user: User | null | undefined,
  deviceFingerprint: string,
  now: number = Date.now(),
): boolean {
  if (!session || !user) return false;
  if (session.userId !== user.id) return false;
  if (session.deviceFingerprint !== deviceFingerprint) return false;
  if (!user.isApproved || user.status !== 'active') return false;
  return !isSessionExpired(session, now);
}
